import type { Address } from '@ahm/api-wrappers-core';
import { setCookie } from 'cookies-next';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { logger } from './logger';
import { createSelectors } from './selectors';

export const CITIES = [
  { label: 'Hà Nội', value: 'HAN', slug: 'ha-noi' },
  { label: 'Đà Nẵng', value: 'DAD', slug: 'da-nang' },
  { label: 'Hồ Chí Minh', value: 'SGN', slug: 'ho-chi-minh' },
  { label: 'Tam Kỳ', value: 'VCL', slug: 'tam-ky' },
];

export type AiStoreOrderState = {
  isOpenMenu: boolean;
  city_id: string | null;
  address: Address | null;
  isLoading: boolean;
  skipAutoLocation: boolean; // Flag để tắt auto location
  requireAddressSync: boolean; // Flag để báo cần sync address
};

export interface AiStoreOrderActions {
  setIsOpenMenu: (isOpen: boolean) => void;
  setCityId: (city_id: string) => void;
  setAddress: (address: Address | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setSkipAutoLocation: (skip: boolean) => void;
  setRequireAddressSync: (require: boolean) => void;
  syncAddressToCookie: () => void; // Force sync address to cookie
}

const AHAFOOD_STORE_NAME = 'ai.store';

export const ahafoodStore = create<
  AiStoreOrderState & AiStoreOrderActions,
  [
    ['zustand/devtools', AiStoreOrderState & AiStoreOrderActions],
    ['zustand/persist', Pick<AiStoreOrderState, 'city_id'>],
  ]
>(
  logger(
    devtools(
      persist(
        (set, get) => {
          const initialState: AiStoreOrderState = {
            city_id: null,
            address: null,
            isLoading: false,
            isOpenMenu: false,
            skipAutoLocation: false,
            requireAddressSync: false,
          };

          return {
            ...initialState,
            setIsOpenMenu: (isOpen) => set({ isOpenMenu: isOpen }),
            setIsLoading: (isLoading: boolean) => set({ isLoading }),
            setAddress: (address: Address | null) => {
              set({ address, requireAddressSync: false });
              // Save address to cookie for server usage
              if (address) {
                try {
                  void setCookie('ahafood_address', JSON.stringify(address), {
                    maxAge: 30 * 24 * 60 * 60,
                  });
                } catch (e) {
                  // ignore
                }
              }
            },
            setCityId: (city_id: string) => {
              set({ city_id });

              // Save cityId to cookie for server usage
              try {
                void setCookie('ahafood_city_id', city_id, {
                  maxAge: 30 * 24 * 60 * 60, // 30 days
                });
              } catch (e) {
                // ignore
              }
            },
            setSkipAutoLocation: (skip: boolean) =>
              set({ skipAutoLocation: skip }),
            setRequireAddressSync: (require: boolean) =>
              set({ requireAddressSync: require }),
            syncAddressToCookie: () => {
              const state = get();
              if (state.address) {
                try {
                  void setCookie(
                    'ahafood_address',
                    JSON.stringify(state.address),
                    {
                      maxAge: 30 * 24 * 60 * 60,
                    }
                  );
                  set({ requireAddressSync: false });
                } catch (e) {
                  console.error('Failed to sync address to cookie:', e);
                }
              }
            },
          };
        },
        {
          name: AHAFOOD_STORE_NAME,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            address: state.address,
            city_id: state.city_id,
          }),
        }
      )
    )
  )
);

export const useAiFoodOrderStore = createSelectors(ahafoodStore);
